import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Facebook, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const SOCIAL_LINKS_KEY = 'nm_social_links';

const SOCIAL_DEFAULTS = {
  instagram: 'https://instagram.com/ndalemmataram',
  facebook:  '',
  tiktok:    '',
  booking:   'https://www.booking.com/hotel/id/ndalem-mataram.html',
  trip:      'https://www.trip.com/hotels/',
  agoda:     '',
};

function useSocialLinks() {
  const [links, setLinks] = useState(SOCIAL_DEFAULTS);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SOCIAL_LINKS_KEY);
      if (saved) setLinks({ ...SOCIAL_DEFAULTS, ...JSON.parse(saved) });
    } catch {}
  }, []);
  return links;
}

export default function Footer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language; // Mendapatkan bahasa yang sedang aktif
  
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const social = useSocialLinks();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      // Asumsi ada newsletterAPI, jika tidak ada bisa disesuaikan
      if (typeof newsletterAPI !== 'undefined') {
         await newsletterAPI.subscribe(email);
      }
      toast.success(t('newsletter.success', { defaultValue: 'Berhasil berlangganan!' }));
      setEmail('');
    } catch {
      toast.error(t('common.error', { defaultValue: 'Terjadi kesalahan.' }));
    } finally { setSubscribing(false); }
  };

  return (
    <footer className="bg-dark text-white">
      <div className="h-1 bg-gradient-to-r from-earth-900 via-accent-500 to-earth-900" />

      {/* Main footer */}
      <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand */}
        <div className="lg:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-accent-700/50 bg-white flex-shrink-0 flex items-center justify-center">
              <img
                src="/images/logo-ndalem.jpg"
                alt="Ndalem Mataram"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<span style="font-weight:900;font-size:13px;color:#92400e;display:flex;align-items:center;justify-content:center;height:100%">NM</span>';
                }}
              />
            </div>
            <div>
              <div className="text-white font-display text-lg font-bold">Ndalem Mataram</div>
              <div className="text-accent-400/80 text-[10px] font-sans tracking-[0.2em] uppercase">Banyuwangi</div>
            </div>
          </div>
          <p className="text-white/50 text-sm font-sans leading-relaxed mb-5">
            {lang === 'id' ? 'Homestay tradisional Jawa modern dengan sentuhan budaya Banyuwangi yang autentik.' 
              : lang === 'zh' ? '现代爪哇传统民宿，融合原汁原味的外南梦文化。' 
              : 'Modern Javanese traditional homestay with an authentic touch of Banyuwangi culture.'}
          </p>

          {/* Social */}
          <div className="space-y-3">
            <p className="text-white/40 text-xs font-sans uppercase tracking-wider">
              {lang === 'id' ? 'Ikuti Kami' : lang === 'zh' ? '关注我们' : 'Follow Us'}
            </p>
            <div className="flex gap-2 flex-wrap">
              {social.instagram && (
                <SocialBtn href={social.instagram} title="Instagram" bg="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400">
                  <Instagram size={15} />
                </SocialBtn>
              )}
              {social.facebook && (
                <SocialBtn href={social.facebook} title="Facebook" bg="bg-blue-700">
                  <Facebook size={15} />
                </SocialBtn>
              )}
              {social.tiktok && (
                <SocialBtn href={social.tiktok} title="TikTok" bg="bg-black">
                  <span className="text-xs font-bold">♪</span>
                </SocialBtn>
              )}
              <SocialBtn href="https://wa.me/6281378575758?text=Hello%20Ndalem%20Mataram%2C%20I%20am%20interested%20in%20staying%20at%20your%20property%20and%20would%20like%20to%20inquire%20about%20your%20homestay%20services%20and%20room%20availability.%20Could%20you%20please%20assist%20me%3F%20Thank%20you." title="Book Now" bg="bg-green-600">
                <MessageCircle size={15} />
              </SocialBtn>
            </div>

            {/* OTA Buttons */}
            {(social.booking || social.trip || social.agoda) && (
              <>
                <p className="text-white/40 text-xs font-sans uppercase tracking-wider pt-1">
                  {lang === 'id' ? 'Pesan Lewat' : lang === 'zh' ? '通过以下方式预订' : 'Book Via'}
                </p>
                <div className="flex flex-col gap-2">
                  {social.booking && (
                    <OTALink href={social.booking} label="Booking.com" dotColor="bg-blue-400" bg="bg-blue-700" />
                  )}
                  {social.trip && (
                    <OTALink href={social.trip} label="Trip.com" dotColor="bg-sky-300" bg="bg-sky-600" />
                  )}
                  {social.agoda && (
                    <OTALink href={social.agoda} label="Agoda" dotColor="bg-red-300" bg="bg-red-600" />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Kamar & Layanan (Sudah Multibahasa) */}
        <div>
          <h4 className="text-white font-display text-base mb-5 after:block after:w-8 after:h-0.5 after:bg-accent-500 after:mt-2">
            {lang === 'id' ? 'Kamar & Layanan' : lang === 'zh' ? '客房与服务' : 'Rooms & Services'}
          </h4>
          <ul className="space-y-2.5">
            <FooterLink to="/rooms">{lang === 'id' ? 'Kamar Kami' : lang === 'zh' ? '我们的客房' : 'Our Rooms'}</FooterLink>
            <FooterLink to="/facilities">{lang === 'id' ? 'Fasilitas' : lang === 'zh' ? '设施' : 'Facilities'}</FooterLink>
            <FooterLink to="/attractions">{lang === 'id' ? 'Wisata Terdekat' : lang === 'zh' ? '附近景点' : 'Nearby Attractions'}</FooterLink>
          </ul>
        </div>

        {/* Tentang */}
        <div>
          <h4 className="text-white font-display text-base mb-5 after:block after:w-8 after:h-0.5 after:bg-accent-500 after:mt-2">
            {t('footer.about', { defaultValue: lang === 'id' ? 'Tentang' : lang === 'zh' ? '关于' : 'About' })}
          </h4>
          <ul className="space-y-2.5">
            <FooterLink to="/about">{t('footer.about_us', { defaultValue: lang === 'id' ? 'Tentang Kami' : lang === 'zh' ? '关于我们' : 'About Us' })}</FooterLink>
            <FooterLink to="/contact">{t('footer.contact_us', { defaultValue: lang === 'id' ? 'Hubungi Kami' : lang === 'zh' ? '联系我们' : 'Contact Us' })}</FooterLink>
            <FooterLink to="/gallery">{lang === 'id' ? 'Galeri' : lang === 'zh' ? '画廊' : 'Gallery'}</FooterLink>
            <FooterLink to="/faq">{t('footer.faq', { defaultValue: 'FAQ' })}</FooterLink>
            <FooterLink to="/privacy">{t('footer.privacy', { defaultValue: lang === 'id' ? 'Privasi & Disclaimer' : lang === 'zh' ? '隐私与免责声明' : 'Privacy & Disclaimer' })}</FooterLink>
            <FooterLink to="/terms">{t('footer.terms', { defaultValue: lang === 'id' ? 'Syarat & Ketentuan' : lang === 'zh' ? '条款和条件' : 'Terms & Conditions' })}</FooterLink>
          </ul>
        </div>

        {/* Kontak */}
        <div>
          <h4 className="text-white font-display text-base mb-5 after:block after:w-8 after:h-0.5 after:bg-accent-500 after:mt-2">
            {t('footer.contact', { defaultValue: lang === 'id' ? 'Kontak' : lang === 'zh' ? '联系' : 'Contact' })}
          </h4>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <MapPin size={15} className="text-accent-400 mt-0.5 shrink-0" />
              <span className="text-white/60 text-sm font-sans leading-relaxed">
                Jl. K.H. Agus Salim No.18, Taman Baru, Banyuwangi, Jawa Timur 68416
              </span>
            </li>
            <li className="flex gap-3 items-center">
              <Phone size={15} className="text-accent-400 shrink-0" />
              <a href="tel:+6281378575758" className="text-white/60 text-sm font-sans hover:text-white transition-colors">
                +62 813-7857-5758
              </a>
            </li>
            <li className="flex gap-3 items-center">
              <Mail size={15} className="text-accent-400 shrink-0" />
              <a href="mailto:ndalem.mataram1@gmail.com" className="text-white/60 text-sm font-sans hover:text-white transition-colors">
                ndalem.mataram1@gmail.com
              </a>
            </li>
          </ul>
          <div className="mt-5 rounded-lg overflow-hidden h-24 bg-white/5 border border-white/10 flex items-center justify-center">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3948.835848780775!2d114.36442657494553!3d-8.219266191811565!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2dd1452df37b4f8d%3A0xeab5e839218d6e3!2sNdalem%20Mataram%20Homestay!5e0!3m2!1sid!2sid!4v1700000000000!5m2!1sid!2sid"
              width="100%"
              height="250"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Lokasi Ndalem Mataram"
            />
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10 px-6 py-5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/40 font-sans">
          <span>{t('footer.copyright', { defaultValue: '© Ndalem Mataram 2026. Hak Cipta Dilindungi.' })}</span>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-white/70 transition-colors">
              {t('footer.privacy', { defaultValue: 'Privasi & Disclaimer' })}
            </Link>
            <Link to="/terms" className="hover:text-white/70 transition-colors">
              {t('footer.terms', { defaultValue: 'Syarat & Ketentuan' })}
            </Link>
          </div>
        </div>
      </div>

      {/* WhatsApp FAB */}
      <a href="https://wa.me/6281378575758" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 bg-green-500 hover:bg-green-400 text-white px-4 py-3 rounded-full shadow-xl transition-all duration-300 hover:scale-105 group">
        <MessageCircle size={20} />
        <span className="text-sm font-sans font-medium hidden group-hover:inline whitespace-nowrap">
          {t('footer.chat_wa', { defaultValue: 'Chat via WA' })}
        </span>
      </a>
    </footer>
  );
}

function SocialBtn({ href, title, bg, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={title}
      className={`w-9 h-9 rounded-full ${bg} hover:opacity-90 flex items-center justify-center text-white transition-opacity`}>
      {children}
    </a>
  );
}

function OTALink({ href, label, dotColor, bg }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`flex items-center gap-2 px-3 py-1.5 ${bg} hover:opacity-90 text-white text-xs font-sans font-semibold rounded-lg transition-opacity`}>
      <span className={`w-2 h-2 rounded-full ${dotColor}`} />
      {label}
    </a>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="text-white/55 text-sm font-sans hover:text-accent-400 transition-colors">
        {children}
      </Link>
    </li>
  );
}